import { useState, useEffect, useRef } from "react";
import { Building2, Upload, Globe, Clock, Briefcase } from "lucide-react";
import { Button } from "@/components/ui/button";
import { InputVenture } from "@/components/ui/input-venture";
import { VentureCard, VentureCardHeader, VentureCardTitle, VentureCardContent } from "@/components/ui/venture-card";
import { supabase } from "@/integrations/supabase/client";
import { useRole } from "@/contexts/RoleContext";
import { toast } from "sonner";

const TIMEZONES = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Anchorage", label: "Alaska Time (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii Time (HT)" },
];

const INDUSTRIES = [
  "Technology",
  "Healthcare",
  "Finance",
  "Real Estate",
  "E-Commerce",
  "Education",
  "Professional Services",
  "Other",
];

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

export default function CompanyProfileSettings() {
  const { clientId } = useRole();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [companyName, setCompanyName] = useState("");
  const [website, setWebsite] = useState("");
  const [timezone, setTimezone] = useState("America/Los_Angeles");
  const [industry, setIndustry] = useState("");
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const fetchCompanyData = async () => {
      if (!clientId) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from("clients")
        .select("company_name, website, timezone, industry, logo_url")
        .eq("id", clientId)
        .single();

      if (error) {
        console.error("Failed to fetch company data:", error);
        toast.error("Failed to load company profile");
      } else if (data) {
        setCompanyName(data.company_name || "");
        setWebsite(data.website || "");
        setTimezone(data.timezone || "America/Los_Angeles");
        setIndustry(data.industry || "");
        setLogoUrl(data.logo_url || null);
      }

      setLoading(false);
    };

    fetchCompanyData();
  }, [clientId]);

  const getInitials = () => {
    if (!companyName) return "?";
    return companyName
      .split(" ")
      .map((w) => w[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !clientId) return;

    if (file.size > MAX_FILE_SIZE) {
      toast.error("File size must be less than 2MB");
      return;
    }

    if (!["image/png", "image/jpeg", "image/gif"].includes(file.type)) {
      toast.error("Only PNG, JPG, and GIF files are allowed");
      return;
    }

    setUploading(true);
    try {
      const ext = file.name.split(".").pop();
      const path = `${clientId}/${Date.now()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from("client-logos")
        .upload(path, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("client-logos")
        .getPublicUrl(path);

      const publicUrl = urlData.publicUrl;

      const { error: updateError } = await supabase
        .from("clients")
        .update({ logo_url: publicUrl })
        .eq("id", clientId);

      if (updateError) throw updateError;

      setLogoUrl(publicUrl);
      toast.success("Logo uploaded successfully");
    } catch (error: any) {
      toast.error(error.message || "Failed to upload logo");
    } finally {
      setUploading(false);
      // Reset file input so the same file can be re-selected
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSave = async () => {
    if (!clientId) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from("clients")
        .update({
          company_name: companyName,
          website,
          timezone,
          industry: industry || null,
          logo_url: logoUrl,
        })
        .eq("id", clientId);

      if (error) throw error;
      toast.success("Company profile updated successfully");
    } catch (error: any) {
      toast.error(error.message || "Failed to update company profile");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl">
        <VentureCard>
          <VentureCardHeader>
            <VentureCardTitle>Company Profile</VentureCardTitle>
          </VentureCardHeader>
          <VentureCardContent>
            <div className="animate-pulse space-y-4">
              <div className="flex items-center gap-4">
                <div className="h-20 w-20 rounded-full bg-muted" />
                <div className="space-y-2 flex-1">
                  <div className="h-4 w-32 bg-muted rounded" />
                  <div className="h-3 w-48 bg-muted rounded" />
                </div>
              </div>
              <div className="h-10 bg-muted rounded" />
              <div className="h-10 bg-muted rounded" />
              <div className="h-10 bg-muted rounded" />
              <div className="h-10 bg-muted rounded" />
            </div>
          </VentureCardContent>
        </VentureCard>
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <VentureCard>
        <VentureCardHeader>
          <VentureCardTitle>Company Profile</VentureCardTitle>
        </VentureCardHeader>
        <VentureCardContent className="space-y-6">
          {/* Logo Upload */}
          <div className="flex items-center gap-4">
            <div className="relative">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="relative group"
                disabled={uploading}
              >
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt={companyName}
                    className="w-20 h-20 rounded-full object-cover border-2 border-border"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                    <span className="text-2xl font-bold text-primary-foreground">
                      {getInitials()}
                    </span>
                  </div>
                )}
                <div className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                  <Upload className="w-5 h-5 text-white" />
                </div>
              </button>
              {uploading && (
                <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
            <div>
              <p className="font-medium">Company Logo</p>
              <p className="text-sm text-muted-foreground">
                JPG, PNG or GIF. Max 2MB.
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif"
              className="hidden"
              onChange={handleLogoUpload}
            />
          </div>

          {/* Company Name */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <Building2 className="w-4 h-4 text-muted-foreground" />
              Company Name
            </label>
            <InputVenture
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Your company name"
            />
          </div>

          {/* Website */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <Globe className="w-4 h-4 text-muted-foreground" />
              Website URL
            </label>
            <InputVenture
              type="url"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://yourcompany.com"
            />
          </div>

          {/* Timezone */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <Clock className="w-4 h-4 text-muted-foreground" />
              Timezone
            </label>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="w-full h-11 rounded-md border border-input bg-surface px-3 text-sm focus:outline-none focus:border-secondary focus:ring-2 focus:ring-secondary/20"
            >
              {TIMEZONES.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
          </div>

          {/* Industry */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-muted-foreground" />
              Industry
            </label>
            <select
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              className="w-full h-11 rounded-md border border-input bg-surface px-3 text-sm focus:outline-none focus:border-secondary focus:ring-2 focus:ring-secondary/20"
            >
              <option value="">Select an industry</option>
              {INDUSTRIES.map((ind) => (
                <option key={ind} value={ind}>
                  {ind}
                </option>
              ))}
            </select>
          </div>

          <Button
            variant="gradient"
            className="w-full"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </VentureCardContent>
      </VentureCard>
    </div>
  );
}
